import * as React from 'react';
import {
    root,
    IComponent,
    IComponentViewController,
    IServiceRegistryComponent
} from 'elc-universal-utils';
import { LazyRenderer } from './LazyRenderer';
import {
    ViewDataPreloader,
    IConfigsCollection,
    ITranslationsCollection
} from '../preloader/ViewDataPreloader';
import { runPerformanceMarker } from '../utils/PerformanceMarker';

export type ComponentNodeType = {
    name: string;
    config: string | null;
    component: React.ReactElement | null;
    node: HTMLElement;
    state: string | null;
};

interface IComponentPropsFunctions {
    [key: string]: Function | object | null;
}

interface IComponentProps extends IComponentPropsFunctions {
    config: object;
    viewController: IComponentViewController | null;
    translations: ITranslationsCollection | null;
}

interface IComponentDataState {
    [key: string]: object | number | string;
}

interface IComponentState extends IComponentDataState {
    children: string[];
}

interface IComponentModule {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    default: React.ComponentType<any>;
}
type ComponentLoader = () => Promise<IComponentModule>;

export abstract class ComponentRenderer extends LazyRenderer {
    protected preloader: ViewDataPreloader | null = null;

    public abstract init(initialMarkup?: string): void;

    public setDataPreloader = (preloader: ViewDataPreloader) => {
        this.preloader = preloader;
    };

    public getDataPreloader = () => {
        return this.preloader;
    };

    private getServiceFromViewName = (viewName: string) => {
        const serviceNameKey = Object.keys(root.ServiceRegistry).find(serviceName =>
            root.ServiceRegistry[serviceName].getView(viewName)
        );

        return serviceNameKey && root.ServiceRegistry[serviceNameKey];
    };

    private getElementProps = async (
        node: ComponentNodeType,
        componentView: IComponent
    ): Promise<IComponentProps> => {
        const parsedConfig = node.config ? JSON.parse(node.config) : {};
        const viewController: IComponentViewController | null = componentView.viewController
            ? componentView.viewController()
            : null;

        let translations: ITranslationsCollection | null = null;
        let configs: IConfigsCollection | null = null;
        // Don't overload components for non exported views otherwise will crash
        if (this.getNodeAttribute(node.node, 'data-version')) {
            if (this.preloader) {
                await this.preloader.preload();

                translations = this.preloader.getPreloadedTranslations() || {};
                translations = Object.keys(translations).length === 0 ? null : translations;

                configs = this.preloader.getPreloadedConfigs();
            }
        }
        const fnProps = Object.keys(parsedConfig)
            .filter(configKey => parsedConfig[configKey] === '__event__')
            .reduce((eventHandlers, configKey) => {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const eventHandler = function(...args: any[]) {
                    const event = new CustomEvent(configKey, {
                        detail: args,
                        cancelable: true,
                        bubbles: false
                    });
                    node.node.dispatchEvent(event);
                };

                return { ...eventHandlers, [configKey]: eventHandler };
            }, {});

        return {
            ...fnProps,
            config: { ...configs, ...parsedConfig },
            translations,
            viewController
        };
    };

    private getElementToRender = (
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        registeredComponent: React.ComponentType<any>,
        props: IComponentProps
    ) => {
        return React.createElement(registeredComponent, props);
    };

    private setStateInController = (state: object, controller: IComponentViewController) => {
        const viewState = Object.values(state)[0]; // { [VIEW_NAME]: { ...state } }
        if (viewState && controller.setSSRState) {
            controller.setSSRState(viewState);
        }
    };

    private setControllerId = (config: string | null, controller: IComponentViewController) => {
        if (config && controller.setId) {
            controller.setId(config);
        }
    };

    private handleController = (
        viewController: IComponentViewController | null,
        state: IComponentDataState,
        config: string | null
    ) => {
        try {
            if (viewController) {
                this.setStateInController(state, viewController);
                this.setControllerId(config, viewController);
            }
        } catch (error) {
            console.warn(error);
        }
    };

    private getViewFromService = async (
        service: IServiceRegistryComponent,
        componentName: string,
        componentView?: IComponent
    ) => {
        let componentModule = service.getViewFromCache && service.getViewFromCache(componentName);
        try {
            if (!componentModule && componentView) {
                const componentViewMethod = (componentView.component.load ||
                    componentView.component) as unknown;
                const module = await (componentViewMethod as ComponentLoader)();
                componentModule = module.default;
            }
        } catch (error) {
            console.warn(`Module couldn\'t be loaded: (${componentName})`, error);
        }

        return componentModule;
    };

    private setViewInCache = (
        componentName: string,
        service: IServiceRegistryComponent,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        componentModule?: React.ComponentType<any>
    ) => {
        if (componentModule && service.setViewInCache) {
            service.setViewInCache(componentName, componentModule);
        }
    };

    private saveViewsInCache = async (componentsName: string[]) => {
        await Promise.all(
            componentsName.map(async componentName => {
                try {
                    const service = this.getServiceFromViewName(componentName);
                    if (service) {
                        const componentView = service.getView(componentName);
                        const componentModule = await this.getViewFromService(
                            service,
                            componentName,
                            componentView
                        );
                        this.setViewInCache(componentName, service, componentModule);
                    }
                } catch (error) {
                    console.warn(`Error saving module: (${componentName})`, error);
                }

                return Promise.resolve();
            })
        );

        return Promise.resolve();
    };

    private parseState = (state: string | null) => {
        let parsedState: IComponentState | undefined;
        try {
            if (state) {
                parsedState = JSON.parse(state);
            }
        } finally {
            parsedState = parsedState ? parsedState : { children: [] };
        }

        return parsedState;
    };

    protected getComponentForNode = async (node: ComponentNodeType) => {
        const { state: nodeState, name, config } = node;
        runPerformanceMarker({ markArgument: `get_view_${name}` });
        const service = name ? this.getServiceFromViewName(name) : null;
        if (service) {
            const componentView = service.getView(name);
            const componentModule = await this.getViewFromService(service, name, componentView);
            this.setViewInCache(name, service, componentModule);

            if (componentModule && componentView) {
                const { children: serviceViewChildrenNames, ...state } = this.parseState(nodeState);
                await this.saveViewsInCache(serviceViewChildrenNames);

                const props = await this.getElementProps(node, componentView);
                this.handleController(props.viewController, state, config);
                node.component = this.getElementToRender(componentModule, props);

                return Promise.resolve(node);
            }
        }

        return Promise.resolve(null);
    };

    protected convertNodeToComponentNodeType = (node: HTMLElement) => ({
        component: null,
        config: this.getNodeAttribute(node, 'data-config'),
        name: this.getNodeAttribute(node, 'data-component') || '',
        node,
        state: this.getNodeAttribute(node, 'data-state')
    });
}
